import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MilkMoneyQuery } from '~/QueryHooks/MilkMoneyQuery'
import Layout from '~/components/Layout'
import { useDebounce } from 'use-lodash-debounce';
import { Button, Divider, Dropdown, Grid, Icon, Input, Popup, Progress, Step } from 'semantic-ui-react';
import { FilterWrapper } from '../../DairyFactory/Farmers/style';
import TableComponent from '~/components/TableComponent';
import dayjs from 'dayjs';
import { formatNumberWithoutFractions, getUsersBrowserData, handleErrors, parseDate } from '~/utils/helpers';
import notify from '~/components/Notify';
import MilkMoneyAccountingService, { FarmerInvoiceType } from '~/api/services/milkMoneyAccounting';
import ENV from '~/environment';
import { DownloadService } from '~/api/services/downloadFiles';
import Dialog from '~/components/Dialog';
import { CustomRow, StyledGrid, StyledSegment } from '../../Survey/ShowSurvey/style';
import { StyledGridColumns, StyledGridRow } from '../../MilkFixedPrice/ShowMilkFixedPrice/style';
import CreateBankFile from './CreateBankFile';
import { actions, milkPaymentStatus, getSteps, getTableColumnHeaders } from './constants';
import ExcelExport from '~/components/ExcelExport';
import MGAModal from './MGAModal';
import { useAuth } from '~/routes/components/AuthProvider';
import { LIGHT_USER_ROLE_SLUG } from '~/Constants';
import { ActionsWrapper, ProgressWrapper, StepDescr, StyledDimmer } from './style';
import GenerateMilkBill from './GenerateMilkBill';
import CloseMonth from './CloseMonth';
import { LazyLottie } from '~/components/LazyLottie';
import ArticlesImportModal from './ArticlesImportModal';
import GeneratePdfs from './GeneratePdfs';
import { useHash } from '~/components/useHash';
import Report from './Report';

const Invoice: React.FC = () => {
  const { t } = useTranslation()
  const increment = useRef(null);
  const intervalPdfs = useRef(null);
  const { systemData: { logged: { roleSlug } } } = useAuth()
  const lighUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG
  const steps = getSteps()
  const [hash] = useHash();

  const [tableColumnHeaders, setTableColumnHeaders] = useState(getTableColumnHeaders())
  const [isOpenCloseMonthDialog, setIsOpenCloseMonthDialog] = useState(false)
  const [dataFiltered, setDataFiltered] = useState({
    search: '',
    month: '',
    rows: 20,
    page: 0
  })
  const [search, setSearch] = useState('')
  const [isLoadingPdfFiles, setIsLoadingPdfFiles] = useState(false)
  const [selectedMonth, setSelectedMonth] = useState('')
  const [openExportBankModal, setExportBankModal] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [isBusySepaXML, setIsBusySepaXML] = useState(false)
  const [openGeneratingModal, setOpenGeneratingModal] = useState(false)
  const [isOpenGeneratingPdfs, setIsOpenGeneratingPdfs] = useState(false)
  const [isArticlesImport, setIsArticlesImports] = useState(false)
  const debouncedValue = useDebounce(search, 300);
  const [openMGA, setOpenMGA] = useState(false)
  const [loadingAction, setLoadingAction] = useState({
    type: '',
    id: null,
    value: false,
    idKey: ''
  })

  const {
    data: invoiceSettings,
    isLoading: isBusySettings,
    isFetching: isFetchingSettings,
    refetch: fetchSettings
  } = MilkMoneyQuery.getInvoiceSettings({ month: selectedMonth })
  const userData = getUsersBrowserData()

  const invoiceInDefaultStatus = invoiceSettings?.status >= milkPaymentStatus.verificationStatus

  const generatingFile = invoiceSettings?.status === milkPaymentStatus.generatingStatus

  const {
    data: invoices,
    isLoading: isBusy,
    refetch: fetchInvoices,
    isFetching: isFetchingData
  } = MilkMoneyQuery.getInvoices(
    { ...dataFiltered, page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1, month: selectedMonth },
    invoiceInDefaultStatus
  )

  useEffect(() => {
    return () => {
      clearInterval(increment.current)
      clearInterval(intervalPdfs.current)
    }
  }, [])

  useEffect(() => {
    fetchInvoices()
  }, [dataFiltered])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedValue, page: 0 })
  }, [debouncedValue])

  useEffect(() => {
    if (invoiceSettings && selectedMonth === '' && !isFetchingSettings) {
      const selectedMonth = hash !== '' ? hash.toString().split('#')[1] : invoiceSettings?.selectedMonth
      setSelectedMonth(selectedMonth)
    }
    if (invoiceSettings?.status > milkPaymentStatus.generatingStatus && generating) {
      notify(t('The milk bill statements for the month {{selectedMonth}} have been successfully created and can now be verified.', { selectedMonth }), 'success')
      setGenerating(false)
      clearInterval(increment.current)
    } else if (invoiceSettings?.status === milkPaymentStatus.generatingStatus && !increment.current) {
      increment.current = setInterval(() => {
        fetchSettings()
      }, 10000)
    }

    //generate pdf files
    if(
      invoiceSettings?.status >= milkPaymentStatus.verificationStatus &&
      invoiceSettings.generatePdfJob &&
      !intervalPdfs.current
    ) {
      intervalPdfs.current = setInterval(() => {
        fetchSettings()
      }, 10000)
    } else if(invoiceSettings?.status >= milkPaymentStatus.verificationStatus &&
      !isFetchingSettings &&
      !invoiceSettings?.generatePdfJob &&
      intervalPdfs.current &&
      isLoadingPdfFiles
    ) {
      setIsLoadingPdfFiles(false)
      clearInterval(intervalPdfs.current)
    }
  }, [invoiceSettings, isFetchingSettings, hash])

  useEffect(() => {
    fetchSettings()
    fetchInvoices()
  }, [selectedMonth, t])

  function renderFilters() {
    return <FilterWrapper>
      <Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Search...')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
    </FilterWrapper>
  }

  async function handleGenerateInvoice(data?) {
    try {
      setGenerating(true)
      await MilkMoneyAccountingService.generateInvoices({ month: selectedMonth, ...data })
      setOpenGeneratingModal(false)
      fetchSettings()
      increment.current = setInterval(() => {
        fetchSettings()
      }, 10000)
    } catch (error) {
      setGenerating(false)
      clearInterval(increment.current)
      handleErrors(error)
    }
  }

  async function handleGenerateFiles(businessPaper) {
    try {
      setIsOpenGeneratingPdfs(false)
      setIsLoadingPdfFiles(true)
      await MilkMoneyAccountingService.generatePdfFiles({ month: selectedMonth, businessPaper })
      intervalPdfs.current = setInterval(() => {
        fetchSettings()
      }, 10000)
    } catch (error) {
      setIsLoadingPdfFiles(false)
      clearInterval(intervalPdfs.current)
      handleErrors(error)
    }
  }

  function renderGenerateMgaPdf(isLoadingFiles) {
    if(isLoadingFiles) {
      return <ProgressWrapper style={{ width: 202, height: 37 }}>
        <Progress
          success
          percent={invoiceSettings?.generatePdfJob?.percentage}
        />
      </ProgressWrapper>
    }

    return <Button
      buttonType={'popup'}
      labelPosition='left'
      onClick={() => setIsOpenGeneratingPdfs(true)}
      icon
    >
      <Icon name={'server'} style={{ color: 'white' }} />
      {t('Create MGA PDF {{percentage}}', { percentage: isLoadingFiles ? `(${invoiceSettings?.generatePdfJob?.percentage} %)` : '' })}
    </Button>
  }

  function renderActions() {
    const displayBankFileAction = invoiceSettings?.status === milkPaymentStatus.verificationStatus
    const displayBankFileActionExport = invoiceSettings?.status >= milkPaymentStatus.verificationStatus
    const closeMonth = invoiceSettings?.status === milkPaymentStatus.preclosingStatus
    const exportFile = invoiceSettings?.status >= milkPaymentStatus.preclosingStatus
    const importArticles = invoiceSettings?.status === milkPaymentStatus.defaultStatus
    const displayGenerateFiles = invoiceSettings?.status >= milkPaymentStatus.verificationStatus ||
    (invoiceSettings?.file ? invoiceSettings?.status < milkPaymentStatus.closedStatus : false)

    const displayDownloadAll = invoiceSettings?.status >= milkPaymentStatus.verificationStatus && Boolean(invoiceSettings?.file)
    const isLoadingFiles = isLoadingPdfFiles || Boolean(invoiceSettings?.generatePdfJob)

    return <ActionsWrapper>
      {displayGenerateFiles && renderGenerateMgaPdf(isLoadingFiles) }
      {displayDownloadAll && <ExcelExport
        url={'milk-money/milk-payment/final/invoices/preview/pdf'}
        fileName={'pdfs'}
        downloadWithFileName
        disabled={isLoadingFiles}
        data={{ month: selectedMonth }}
        icon={'cloud download'}
        name={t('Download MGA PDF')}
      />}
      {importArticles && <Button
        onClick={() => setIsArticlesImports(true)}
      >
        {t('Import Article Bookings')}
      </Button>}
      {displayGenerateButton && <Button
        loading={generating}
        disabled={generating}
        onClick={() => setOpenGeneratingModal(true)}
      >{t('Generate your milk bill')}</Button>}
      {/*   {displayBankFileActionExport && <ExcelExport
        url={'milk-money/milk-payment/invoices/preview/pdf'}
        fileName={'mga_details'}
        buttonType={'popup'}
        popupContent={'Vorschau aller Milchgeldabrechnungen in diesem Abrechnungsmonat.'}
        data={{ month: selectedMonth }}
        icon={'eye'}
        name={t('Preview')}
      />} */}
      {displayBankFileAction && <Popup
        content={'Generierung der SEPA XML Datei für alle Milchgeldabrechnungen in diesem Abrechnungsmonat.'}
        trigger={<Button
          loading={generating}
          disabled={generating || Boolean(invoiceSettings.hasErrors)}
          buttonType={'popup'}
          labelPosition='left'
          icon
          onClick={() => setExportBankModal(true)}
        >
          <Icon name={'euro sign'} style={{ color: 'white' }} />
          {t('Sepa XML')}
        </Button>
        }
      />}
      {closeMonth && <Button
        onClick={() => setIsOpenCloseMonthDialog(true)}
      >{t('Close billing month')}</Button>}
      {exportFile && <ExcelExport
        url={'milk-money/milk-payment/final/bank-file/sepa/export'}
        data={{ month: selectedMonth }}
        fileName={'sepa_xml'}
        name={t('Download SEPA XML')}
      />}
      {displayBankFileActionExport && <Popup
        content={'Ausdruck aller Milchgeldabrechnungen für den Abrechnungsmonat.'}
        trigger={<Button
          onClick={() => setOpenMGA(true)}
          icon={true}
          labelPosition={'left'}
        >
          <Icon className={'print'} style={{ color: 'white' }} />
          {t('Sample print')}
        </Button>}
      />}
      <Report
        verificationStatus={invoiceSettings?.status >= milkPaymentStatus.verificationStatus}
        selectedMonth={selectedMonth}
      />
    </ActionsWrapper>
  }

  async function handleDownloadPdf(row: FarmerInvoiceType, type) {
    setLoadingAction({
      type: type,
      id: row.id,
      value: true,
      idKey: 'id'
    })
    try {
      await DownloadService.downloadFileWithName(`milk-money/milk-payment/final/invoices/${row.id}/preview/pdf`)
    } catch (error) {
      handleErrors(error)
    }
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  async function handlePreview(id) {
    setLoadingAction({
      type: 'preview',
      id: id,
      value: true,
      idKey: 'id'
    })
    await fetch(`${ENV.API_URL}/milk-money/milk-payment/final/invoices/${id}/preview/pdf`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': localStorage.getItem('token') }
      }
    ).then(response => response.blob()).then(response => {
      const csvURL = window.URL.createObjectURL(response);
      setPreviewUrl(csvURL)
    })
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  async function handleRecalculateInvoiceFile(row, type) {
    setLoadingAction({
      type: type,
      id: row.id,
      value: true,
      idKey: 'id'
    })
    try {
      await MilkMoneyAccountingService.reacalculateInvoiceFile(row.id)
      notify(t('The milk bill for the farmer {{supplierNo}} was successfully recalculated and created.', { supplierNo: row?.supplierNo }), 'success')
      fetchSettings()
      fetchInvoices()
    } catch (error) {
      handleErrors(error)
    }
    setLoadingAction({
      type: '',
      id: null,
      value: false,
      idKey: ''
    })
  }

  function handleActionClick(row: FarmerInvoiceType, type) {
    switch (type) {
      case 'preview':
        return handlePreview(row.id)
      case 'download':
        return handleDownloadPdf(row, type)
      case 'recalculate':
        return handleRecalculateInvoiceFile(row, type)
      default:
        return
    }
  }

  function renderTable() {
    const editAvailable = invoiceSettings?.status === milkPaymentStatus.verificationStatus
    const actionsFiltered = editAvailable ? actions : actions.filter(action => action.type !== 'link').filter(action => action.actionType !== 'recalculate')

    return <>
      <div style={{ marginBottom: 10, marginTop: 20 }}>
        {renderFilters()}
      </div>
      <TableComponent
        isFetchingData={isFetchingData}
        animated
        loading={isFetchingSettings || isBusySettings}
        loadingAction={loadingAction}
        actions={actionsFiltered}
        tableColumnHeaders={tableColumnHeaders}
        dataFiltered={dataFiltered}
        handleClickAction={(row, type) => handleActionClick(row, type)}
        handleChangeDataFiltered={(data, tableColumnHeaders) => {
          setDataFiltered(data)
          setTableColumnHeaders(tableColumnHeaders)
        }}
        rows={rows}
        total={filtered}
      />
    </>
  }

  function renderDetailInvoice() {
    return <Step.Group stackable='tablet' columns={2} widths={'5'}>
      {steps.map((step, index) => {
        const lastStep = index === steps.length - 1
        const statusActive = invoiceSettings?.status === step.statusValue
        const completedStatus = lastStep ? invoiceSettings?.status === step.statusValue : invoiceSettings?.status > step.statusValue
        return <Step
          style={{ height: 75 }}
          completed={completedStatus}
          active={statusActive}
          key={step.description}
        >
          {(!statusActive || lastStep) && <Icon
            name={completedStatus ? 'checkmark' : 'close'}
            style={{ color: completedStatus ? 'green' : 'red' }}
          />}
          <Step.Content>
            <Step.Title>{completedStatus ? step.titleAfterFinished : step.title}</Step.Title>
            {statusActive && <StepDescr>
              {step.description}
            </StepDescr>}
          </Step.Content>
        </Step>
      })}
    </Step.Group>
  }

  function renderGeneratingModal() {
    const monthDate = invoiceSettings?.months?.find(month => month.key === selectedMonth)
    return <GenerateMilkBill
      openGeneratingModal={openGeneratingModal}
      handleClose={() => setOpenGeneratingModal(false)}
      selectedMonth={monthDate?.label}
      handleGenerateInvoice={handleGenerateInvoice}
    />
  }

  function getIcon(value) {
    return <Icon
      name={value ? 'checkmark' : 'close'}
      color={value ? 'green' : 'red'}
    />
  }

  function renderVerificationDetails() {
    return <StyledSegment placeholder>
      <StyledGrid columns={2} stackable>
        <Divider vertical style={{ margin: 0 }} />
        <Grid.Column>
          <Grid columns={2} stackable>
            <CustomRow>
              <Grid.Column>{t('Billing month')}</Grid.Column>
              <StyledGridColumns>{dayjs(invoiceSettings?.selectedMonth).format('MMM YYYY')}</StyledGridColumns>
            </CustomRow>
            <StyledGridRow>
              <Grid.Column>{t('Total delivered amount (in l)')}</Grid.Column>
              <StyledGridColumns>{formatNumberWithoutFractions(invoiceSettings?.totalDeliveredAmountInLiters)}</StyledGridColumns>
            </StyledGridRow>
            <StyledGridRow>
              <Grid.Column>{t('Total delivered amount (in kg)')}</Grid.Column>
              <StyledGridColumns>{formatNumberWithoutFractions(invoiceSettings?.totalDeliveredAmountInKg)}</StyledGridColumns>
            </StyledGridRow>
          </Grid>
        </Grid.Column>
        <Grid.Column>
          <Grid columns={2} stackable style={{ marginLeft: 5 }}>
            <StyledGridRow>
              <Grid.Column>{t('MPR data available?')}</Grid.Column>
              <StyledGridColumns>{getIcon(invoiceSettings.milkSamplesImported)}</StyledGridColumns>
            </StyledGridRow>
            <StyledGridRow>
              <Grid.Column>{t('Base price deposited?')}</Grid.Column>
              <StyledGridColumns>{getIcon(invoiceSettings.basePricesSet)}</StyledGridColumns>
            </StyledGridRow>
          </Grid>
        </Grid.Column>
      </StyledGrid>
    </StyledSegment>
  }

  function renderPreviewModal() {
    return <Dialog
      isDialogOpen={Boolean(previewUrl)}
      textHeader={t('Preview')}
      textClose={t('Close')}
      type={'preview'}
      textContent={<iframe width='100%' height='100%' src={previewUrl}></iframe>}
      handleClose={() => setPreviewUrl('')}
    />
  }

  function renderMonthFilter() {
    return <Dropdown
      selection
      selectOnBlur={false}
      placeholder={t('Select month')}
      value={selectedMonth}
      options={invoiceSettings?.months?.map(month => ({ value: month?.key, text: month?.label }))}
      onChange={(e, data) => setSelectedMonth(data.value as string)}
    />
  }

  function renderCloseMonthModal() {
    return <CloseMonth
      isOpenCloseMonthDialog={isOpenCloseMonthDialog}
      handleClose={(submitted?) => {
        if (submitted) {
          fetchSettings()
        }
        setIsOpenCloseMonthDialog(false)
      }}
      selectedMonth={month}
    />
  }

  async function handleGenerateBankFile(date, text, data) {
    setExportBankModal(false)
    setIsBusySepaXML(true)
    try {
      await MilkMoneyAccountingService.generateBankFile(
        {
          date: parseDate(date),
          text,
          month: month?.key,
          ...userData,
          ...data
        }
      )
      await fetchSettings()
      notify(t('The SEPA XML file for the month {{month}} has been successfully created and is available for download.', { month: month?.label }), 'success')
    } catch (error) {
      handleErrors(error)
    }
    setIsBusySepaXML(false)
  }

  function renderGenerateFileLoader() {
    return <>
      <LazyLottie
        getAnimationData={() => import('@assets/json-lottie/file-transfer.json')}
        loop
        id='file-transfer'
        style={{ height: 250, width: 250 }}
      />
      <div style={{ color: 'black' }}>{t('Please be patient, the milk bill statements are currently being prepared.')}</div>
    </>
  }

  function renderArticlesImport() {
    return <ArticlesImportModal
      isArticlesImportOpen={isArticlesImport}
      handleClose={() => setIsArticlesImports(false)}
      selectedMonth={month}
    />
  }

  function renderGeneratePdfs() {

    return <GeneratePdfs
      isOpen={isOpenGeneratingPdfs}
      handleClose={() => setIsOpenGeneratingPdfs(false)}
      handleSubmit={(businessPaper) => handleGenerateFiles(businessPaper)}
    />
  }

  const { filtered, rows } = invoices ? invoices : { filtered: 0, rows: [] };

  const displayGenerateButton = Boolean(invoiceSettings?.basePricesSet) && Boolean(invoiceSettings?.milkSamplesImported) &&
    invoiceSettings?.status === milkPaymentStatus.defaultStatus

  const month = invoiceSettings?.months?.find(month => month.key === selectedMonth)

  return <Layout
    title={t('MG-Invoice')}
    segmented
    isBusy={isBusySettings || isBusy || isFetchingSettings}
    useSpinner={isFetchingSettings}
    actions={!lighUserLogged && renderActions()}
    filters={invoiceSettings && renderMonthFilter()}
    animatedSpinner={isBusySepaXML ? {
      fileName: 'euro-bank-to-bank-transfer',
      text: t('Please be patient, the SEPA XML file with the booking data is currently being created.')
    } : null}
  >
    {renderArticlesImport()}
    <CreateBankFile
      selectedMonth={month}
      openModal={openExportBankModal}
      handleClose={(submitted = false) => {
        setExportBankModal(false)
        if (submitted) {
          fetchSettings()
        }
      }}
      handleSubmit={(date, text, data?) => handleGenerateBankFile(date, text, data)}
    />
    {renderCloseMonthModal()}
    {renderPreviewModal()}
    {openGeneratingModal && renderGeneratingModal()}
    <div>
      {renderDetailInvoice()}
      {invoiceSettings?.status === milkPaymentStatus.defaultStatus && renderVerificationDetails()}

      {(generatingFile || generating) && <div style={{ position: 'relative', height: 'calc(100vh - 700px)' }}>
        <StyledDimmer>
          {renderGenerateFileLoader()}
        </StyledDimmer>
      </div>}
    </div>
    {renderGeneratePdfs()}
    {invoices && invoiceInDefaultStatus && renderTable()}
    {openMGA && <MGAModal
      open={openMGA}
      handleClose={() => setOpenMGA(false)}
      monthSelected={month}
    />}
  </Layout>
}
export default Invoice